import { ref, watch, computed } from '@vue/composition-api';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import moment from 'moment';
import store from '@/store';

export default function useCohortProfilesList(pathwayData) {
  // Use toast
  const toast = useToast();

  const refSessionListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'name' },
    // {
    //   key: 'courses',
    //   label: 'Courses',
    //   formatter: (value) => {
    //
    //     if (value.length === 0) return 'No courses assigned';
    //
    //     const listOfCourses = value
    //         .map(course => (course.name || '').trim())
    //         .filter((item, i, ar) => ar.indexOf(item) === i)
    //         .join(', ');
    //
    //     return listOfCourses;
    //   },
    // },
    {
      key: 'profiles',
      label: 'Students',
    },
    {
      key: 'createdAt',
      label: 'Created',
      formatter: (value) => {
        return moment(value).format('L');
      },
    },
    { key: 'visibility' },
    { key: 'status' },
    { key: 'actions' },
  ];
  const perPage = ref(10);
  const totalSessions = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('createdAt');
  const isSortDirDesc = ref(true);

  const visibilityFilter = ref(null);
  const statusFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refSessionListTable.value ? refSessionListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalSessions.value,
    };
  });

  const refetchData = () => {
    if (refSessionListTable.value) {
      refSessionListTable.value.refresh();
    }
  };

  watch([currentPage, perPage, searchQuery, visibilityFilter, statusFilter, pathwayData], () => {
    refetchData();
  });

  const fetchSessions = (ctx, callback) => {
    const pw = pathwayData.value;

    if (pw.sessions) {
      callback(pw.sessions.reverse());
      totalSessions.value = pw.sessions.length;
      return;
    }
    callback([]);
    totalSessions.value = 0;
    return;

    const sbv = sortBy.value === 'id' ? '_id' : sortBy.value;
    const sb = `${sbv}:${isSortDirDesc.value ? 'desc' : 'asc'}`;

    const pl = {
      search: searchQuery.value,
      limit: perPage.value,
      page: currentPage.value,
      sortBy: sb,

      populate: 'sessions,sessions.courses,courses,courses.classes',
    };

    if (visibilityFilter.value) {
      pl.visibility = visibilityFilter.value;
    }
    if (statusFilter.value) {
      pl.status = statusFilter.value;
    }

    store
      .dispatch('app-roster/fetchSessions', pl)
      .then((response) => {
        const { results, totalResults } = response.data;

        callback(results);
        totalSessions.value = totalResults;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching sessions list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === 'subscriber') return 'primary';
    if (role === 'author') return 'warning';
    if (role === 'maintainer') return 'success';
    if (role === 'editor') return 'info';
    if (role === 'admin') return 'danger';
    return 'primary';
  };

  const resolveUserRoleIcon = (role) => {
    if (role === 'subscriber') return 'SessionIcon';
    if (role === 'author') return 'SettingsIcon';
    if (role === 'maintainer') return 'DatabaseIcon';
    if (role === 'editor') return 'Edit2Icon';
    if (role === 'admin') return 'ServerIcon';
    return 'SessionIcon';
  };

  const resolveSessionVisibilityVariant = (status) => {
    if (status === 'internal') return 'primary';
    if (status === 'public') return 'info';
    return 'primary';
  };

  const resolveSessionStatusVariant = (status) => {
    if (status === 'created') return 'secondary';
    if (status === 'enrolling') return 'primary';
    if (status === 'waiting') return 'warning';
    if (status === 'live') return 'info';
    if (status === 'completed') return 'success';
    return 'primary';
  };

  const statusOptions = [
    { label: 'Created', value: 'created' },
    { label: 'Enrolling', value: 'enrolling' },
    { label: 'Waiting', value: 'waiting' },
    { label: 'Live', value: 'live' },
    { label: 'Completed', value: 'completed' },
  ];

  return {
    fetchSessions,
    tableColumns,
    perPage,
    currentPage,
    totalSessions,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refSessionListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveSessionVisibilityVariant,
    resolveSessionStatusVariant,
    refetchData,

    statusOptions,

    // Extra Filters
    visibilityFilter,
    statusFilter,
  };
}
